/**
 * /videos/:id — player + metadados + status polling.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import {
  ArrowLeft, Loader2, Trash2, Download, ExternalLink, Captions, AlertCircle, RotateCw,
} from 'lucide-react';

interface VideoFull {
  id: string;
  texto: string;
  avatarId: string;
  voiceId: string;
  aspectRatio: string;
  background: string | null;
  status: string;
  heygenVideoId: string | null;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  duracaoSeg: number | null;
  custoEstimadoUsd: string | number | null;
  custoUsd: string | number | null;
  erro: string | null;
  transcricaoId: string | null;
  enviadoEm: string | null;
  concluidoEm: string | null;
  criadoEm: string;
}

export default function VideoDetalhe() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [v, setV] = useState<VideoFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [reenviando, setReenviando] = useState(false);

  async function carregar() {
    if (!id) return;
    try {
      const r = await api.get<VideoFull>(`/videos/${id}`);
      setV(r);
      setErro(null);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
    // Polling 6s pra status nao-terminal
    const t = setInterval(() => {
      setV((cur) => {
        if (cur && ['concluido', 'falhou', 'cancelado'].includes(cur.status)) {
          return cur;  // ja terminal, nao re-poll
        }
        carregar();
        return cur;
      });
    }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [id]);

  async function deletar() {
    if (!id) return;
    if (!confirm('Apagar este vídeo?')) return;
    try {
      await api.delete(`/videos/${id}`);
      nav('/videos');
    } catch (e) {
      alert((e as Error).message);
    }
  }

  async function tentarNovamente() {
    if (!id || reenviando) return;
    setReenviando(true);
    try {
      const novo = await api.post<VideoFull>(`/videos/${id}/retry`, {});
      nav(`/videos/${novo.id}`);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setReenviando(false);
    }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Carregando...</div>;
  if (erro || !v) return (
    <div className="p-6">
      <Link to="/videos" className="text-sm text-purple-600 flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-sm text-red-700 dark:text-red-400">
        {erro || 'Vídeo não encontrado.'}
      </div>
    </div>
  );

  const ativo = ['pendente', 'enviado', 'processando'].includes(v.status);

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <Link to="/videos" className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>
        <button
          onClick={deletar}
          className="flex items-center gap-1 text-xs px-2 py-1.5 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
        >
          <Trash2 className="w-3.5 h-3.5" /> Apagar
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-4">
        <div className="aspect-video bg-black relative flex items-center justify-center">
          {v.videoUrl ? (
            <video src={v.videoUrl} poster={v.thumbnailUrl ?? undefined} controls className="w-full h-full" />
          ) : v.status === 'falhou' ? (
            <div className="text-center text-red-300 p-6">
              <AlertCircle className="w-10 h-10 mx-auto mb-2" />
              <p className="text-sm">Falha na renderização</p>
            </div>
          ) : (
            <div className="text-center text-white/70 p-6">
              <Loader2 className="w-10 h-10 mx-auto mb-2 animate-spin" />
              <p className="text-sm">{v.status}…</p>
              <p className="text-xs mt-1">Renderização HeyGen pode levar 1-5min</p>
            </div>
          )}
        </div>
      </div>

      {/* Metadados */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
        <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Detalhes</h3>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <Linha label="Status">
            <span className={ativo ? 'text-purple-600 dark:text-purple-400' : v.status === 'concluido' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
              {v.status}
            </span>
          </Linha>
          <Linha label="Proporção">{v.aspectRatio}</Linha>
          {v.duracaoSeg !== null && (
            <Linha label="Duração">{Math.floor(v.duracaoSeg / 60)}:{String(v.duracaoSeg % 60).padStart(2, '0')}</Linha>
          )}
          {v.custoUsd !== null && (
            <Linha label="Custo real">${Number(v.custoUsd).toFixed(4)}</Linha>
          )}
          {v.custoEstimadoUsd !== null && v.custoUsd === null && (
            <Linha label="Custo estimado">~${Number(v.custoEstimadoUsd).toFixed(4)}</Linha>
          )}
          <Linha label="Avatar"><span className="font-mono text-xs">{v.avatarId.slice(0, 16)}…</span></Linha>
          <Linha label="Voz"><span className="font-mono text-xs">{v.voiceId.slice(0, 16)}…</span></Linha>
          {v.heygenVideoId && (
            <Linha label="HeyGen ID"><span className="font-mono text-xs">{v.heygenVideoId.slice(0, 16)}…</span></Linha>
          )}
        </dl>
        {v.erro && (
          <div className="mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2 text-xs text-red-700 dark:text-red-400">
            {v.erro}
          </div>
        )}
        {v.status === 'falhou' && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={tentarNovamente}
              disabled={reenviando}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-50"
              title="Re-submete pra HeyGen com as mesmas configurações"
            >
              {reenviando
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <RotateCw className="w-4 h-4" />
              }
              Tentar novamente
            </button>
          </div>
        )}
      </div>

      {/* Acoes */}
      {v.videoUrl && (
        <div className="flex gap-2 mb-4">
          <a
            href={v.videoUrl}
            download
            className="flex items-center gap-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded"
          >
            <Download className="w-4 h-4" /> Baixar MP4
          </a>
          <a
            href={v.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ExternalLink className="w-4 h-4" /> Abrir
          </a>
        </div>
      )}

      {/* Texto */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
        <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Texto</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
          {v.texto}
        </p>
      </div>

      {/* Origem */}
      {v.transcricaoId && (
        <Link
          to={`/transcricoes/${v.transcricaoId}`}
          className="inline-flex items-center gap-1 text-sm text-purple-600 dark:text-purple-400 hover:underline"
        >
          <Captions className="w-4 h-4" /> Ver transcrição original
        </Link>
      )}
    </div>
  );
}

function Linha({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-gray-900 dark:text-gray-100">{children}</dd>
    </>
  );
}
